import React, {
  useEffect, useRef, useState, useCallback, createContext, useContext,
} from 'react';
import { useBlocker } from 'react-router-dom';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';

/** Warn the browser before a tab close / refresh while there are unsaved
 *  changes. Native dialog — the browser owns the wording. */
export function useBeforeUnload(when) {
  useEffect(() => {
    if (!when) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [when]);
}

/** The one prompt every form shares: Save, Discard, or Keep editing. */
export function ConfirmDiscardDialog({ open, onSave, onDiscard, onCancel, saving }) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent data-testid="unsaved-changes-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>You have unsaved changes</AlertDialogTitle>
          <AlertDialogDescription>
            Save them before leaving, discard them, or keep editing.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onCancel}
            data-testid="unsaved-keep-editing">Keep editing</Button>
          <Button variant="destructive" onClick={onDiscard}
            data-testid="unsaved-discard">Discard</Button>
          {onSave && (
            <Button onClick={onSave} disabled={saving} data-testid="unsaved-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Props a guarded <DialogContent> needs so clicking outside never closes it
 *  and Escape routes through the unsaved-changes check. */
export function guardedContentProps(onEscapeClose) {
  return {
    onInteractOutside: (e) => e.preventDefault(),
    onEscapeKeyDown: (e) => { e.preventDefault(); onEscapeClose(); },
  };
}

// ---------------------------------------------------------------------------
// App-wide navigation guard. Any dirty form registers itself; the provider
// then blocks in-app navigation (menu / link clicks and browser back/forward
// via React Router's useBlocker) and tab close / refresh (native beforeunload),
// showing the shared Save / Discard / Keep-editing prompt.
// ---------------------------------------------------------------------------

const NavGuardCtx = createContext(null);

export function NavigationGuardProvider({ children }) {
  // id -> { getDirty(): bool, save(): Promise<bool|void>, discard(): void }
  const guards = useRef(new Map());
  const [prompt, setPrompt] = useState(null);
  const [saving, setSaving] = useState(false);

  const anyDirty = useCallback(
    () => [...guards.current.values()].some(g => g.getDirty()),
    [],
  );

  const register = useCallback((id, api) => {
    guards.current.set(id, api);
    return () => { guards.current.delete(id); };
  }, []);

  // In-app navigation + browser Back/Forward.
  const blocker = useBlocker(useCallback(
    ({ currentLocation, nextLocation }) =>
      anyDirty() && currentLocation.pathname !== nextLocation.pathname,
    [anyDirty],
  ));

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    setPrompt({
      onSave: async () => {
        setSaving(true);
        try {
          const dirty = [...guards.current.values()].filter(g => g.getDirty());
          let ok = true;
          for (const g of dirty) {
            if (g.save) { const r = await g.save(); if (r === false) ok = false; }
          }
          if (ok) { setPrompt(null); blocker.proceed(); }
        } finally {
          setSaving(false);
        }
      },
      onDiscard: () => {
        [...guards.current.values()].forEach(g => g.discard && g.discard());
        setPrompt(null);
        blocker.proceed();
      },
      onCancel: () => { setPrompt(null); blocker.reset(); },
    });
  }, [blocker]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Tab close / refresh.
  useEffect(() => {
    const handler = (e) => {
      if (anyDirty()) { e.preventDefault(); e.returnValue = ''; return ''; }
      return undefined;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyDirty]);

  return (
    <NavGuardCtx.Provider value={register}>
      {children}
      <ConfirmDiscardDialog
        open={!!prompt}
        onSave={prompt?.onSave}
        onDiscard={prompt?.onDiscard || (() => {})}
        onCancel={prompt?.onCancel || (() => {})}
        saving={saving}
      />
    </NavGuardCtx.Provider>
  );
}

/** Register a dirty form with the app-wide guard. Pass getters/handlers; they
 *  are read through a ref so registration stays stable across renders. */
export function useNavigationGuard(id, { getDirty, save, discard }) {
  const register = useContext(NavGuardCtx);
  const ref = useRef({ getDirty, save, discard });
  ref.current = { getDirty, save, discard };
  useEffect(() => {
    if (!register) return undefined;
    return register(id, {
      getDirty: () => !!ref.current.getDirty && ref.current.getDirty(),
      save: () => (ref.current.save ? ref.current.save() : undefined),
      discard: () => ref.current.discard && ref.current.discard(),
    });
  }, [register, id]);
}
